<template>
	<div class="fullscreen-overlay">
		<h2 class="overlay-header text-centered" style="border-bottom: 1px solid">Process Party - {{ partyId }}</h2>

		<div id="processParty" class="aParty overlay-body">
			<div class="party-user py-1"
				v-for="user in partyUsers"
				v-bind:key="user.id"
				v-bind:id="`user_${user.id}`"
				v-bind:class="{ good: user.areAllItemsDisbursed }">
				<div class="party-user-info px-1 pb-1">
					<div class="party-user-image">
						<img v-bind:src="getUserImage(user.image)" v-bind:alt="user.full_name" onerror="this.src='images/default_user.jpg'"/>
					</div>
					<h4 class="user-name">{{ user.full_name }}</h4>
				</div>

				<div v-for="item in getSortedDisbursements(user.id)" :key="item.party_item_id + item.wasScanned.toString()">
					<div v-bind:class="{ good: item.wasScanned, bad: item.hasError }" class="px-1">
						<PartyItemRow
							:title="item.event_credential_type_title"
							:fa-icon="getDisbursementIcon(item)"
							:is-process-party="true"
						/>

						<PartyItemRow
							v-if="getReservedSeatLabel(item)"
							:title="getReservedSeatLabel(item)"
							:is-process-party="true"
						/>

						<ScanCredential
							v-if="!item.hasError"
							:user_name="user.full_name"
							:party_id="partyId"
							:party_item_id="item.party_item_id"
							:credential_title="item.event_credential_type_title"
							:scans_for_type="item.event_credential_type_effective_scans_for_type"
							:initial_value="item.barcode_or_rfid"
							@onCredentialScanned="(value) => onPartyItemScanned(item.party_item_id, value)"
						/>
					</div>
				</div>
			</div>

			<div v-if="!partyUsers.length" class="grow"></div>

			<div class="button-row">
				<button v-if="hasErrorState" class="red" v-on:click="refresh">Refresh</button>
				<button v-else class="green" v-on:click="submitProcessParty" v-bind:disabled="disableSubmitParty">Submit Party</button>
			</div>
		</div>

		<ErrorPopup
			:show="shouldShowError"
			:error-message="errorMessage"
			:btn-text="errorBtnText"
			@close="closeError"
		/>
	</div>
</template>

<script>
import axios from 'axios';
import PartyItemRow from "./PartyItemRow.vue";
import ErrorPopup from "./ErrorPopup.vue";
import ScanCredential from "@/components/ScanCredential.vue";

export default {
	name: 'ScanCredentials',
	components: {
		PartyItemRow,
		ScanCredential,
		ErrorPopup
	},
	data() {
		return {
			partyId: null,
			partyItemsToProcess: [],
			partyUsers: [],
			//error popup
			shouldShowError: false,
			errorMessage: "",
			errorBtnText: "",
			hasFetchedPartyDisbursements: false
		}
	},
	async mounted() {
		this.partyId = this.$scanner.partyId
		this.getPartyDisbursements()
	},
	computed: {
		disableSubmitParty() {
			const anyUndisbursedUser = this.partyUsers.find(user => !user.areAllItemsDisbursed)
			return !this.hasFetchedPartyDisbursements || !!anyUndisbursedUser
		},
		hasErrorState() {
			return !!this.partyItemsToProcess.find(item => item.hasError === true)
		},
	},
	methods: {
		getPartyDisbursements() {
			window.scanner.loader.open()

			const scannerRegKey = localStorage.getItem('scanner_regkey');
			axios.defaults.headers.common['X-Auth-Token'] = scannerRegKey;
			axios.post(`${this.$scanner.api_server}/api/scannerdevice/party/${this.partyId}/getPaidPartyDisbursements`, {
				check: scannerRegKey,
				party_id: `${this.partyId}`,
			})
				.then(res => {
					if (res.status === 200) {
						const partyUsers = {}
						const partyItemsToProcess = []

						res.data.data.forEach(item => {
							if (!partyUsers[item.user_id]) {
								partyUsers[item.user_id] = {...item.user, areAllItemsDisbursed: false}
							}

							partyItemsToProcess.push({
								credential_type: item.credential_type,
								event_credential_type_effective_scans_for_type: item.event_credential_type_effective_scans_for_type,
								event_credential_type_title: item.event_credential_type_title,
								party_item_id: item.party_item_id,
								user_id: item.user_id,
								barcode_or_rfid: item.barcode_or_rfid,
								wasScanned: !!item.barcode_or_rfid,
								data: item.data,
								hasError: item.has_error,
							})
						})
						this.partyItemsToProcess = partyItemsToProcess

						if(this.hasErrorState) {
							this.showError("Unable to process the party items, please contact a Champion", "OK")
						}

						let allUsers = Object.values(partyUsers);
						const naturalCollator = new Intl.Collator();
						allUsers.sort((a, b) => naturalCollator.compare(a.full_name, b.full_name));
						this.partyUsers = allUsers
						allUsers.forEach(user => this.checkIfUserIsAllGood(user.id))
					} else {
						alert('🎉Party: 🚫Party Not Loaded');
						window.debug(res.data);
					}
				})
				.catch(err => {
					this.showError()
				})
				.finally(() => {
					this.hasFetchedPartyDisbursements = true
					window.scanner.loader.close()
				})
		},
		getUserImage(userImageStr) {
			return userImageStr ? `${window.scanner.api_server}/media/user/${(userImageStr)}` : '/images/default_user.jpg';
		},
		getSortedDisbursements(userId) {
			//Show wristbands at the top of the list
			let userWristbands = this.partyItemsToProcess.filter((item) => item.user_id === userId && item.credential_type === 'wristband')
			let userNonWristbands = this.partyItemsToProcess.filter((item) => item.user_id === userId && item.credential_type !== 'wristband')
			return [...userWristbands, ...userNonWristbands]
		},
		onPartyItemScanned(partyItemId, scannedValue) {
			const partyItem = this.partyItemsToProcess.find((item) => item.party_item_id === partyItemId)
			partyItem.wasScanned = true
			partyItem.barcode_or_rfid = scannedValue
			this.checkIfUserIsAllGood(partyItem.user_id)
		},
		checkIfUserIsAllGood(userId) {
			const anyUnscannedItem = this.partyItemsToProcess.find((item) => item.user_id === userId && !item.wasScanned)
			if (!anyUnscannedItem) {
				const userIndex = this.partyUsers.findIndex(user => user.id === userId)
				this.partyUsers[userIndex].areAllItemsDisbursed = true
			}
		},
		getDisbursementIcon(item) {
			if(item.hasError) {
				return 'exclamation-triangle'
			}

			const credentialType = item.credential_type
			switch (credentialType) {
				case 'wristband':
					return 'circle-notch'
				case 'decal':
					return 'sticky-note'
				case 'access':
					return 'id-badge'
				default:
					return ''
			}
		},
		getReservedSeatLabel(item) {
			if (item.data && item.data.reservation_selected_seat) {
				return `Reserved Site: ${item.data.reservation_selected_seat}`
			}
			return ''
		},
		refresh() {
			window.location.reload()
		},
		submitProcessParty() {
			window.scanner.loader.open()

			const scannerRegKey = localStorage.getItem('scanner_regkey');
			axios.defaults.headers.common['X-Auth-Token'] = scannerRegKey;
			axios.post(`${this.$scanner.api_server}/api/scannerdevice/party/${this.partyId}/submitParty`, {
				check: scannerRegKey,
			})
				.then(res => {
					if (res.data.status.success) {
						this.$scanner.closeBatch()
						localStorage.removeItem('party_id')
						window.scanner.loader.close()
						this.$emit("submitted")
					} else {
						this.showError('Unable to submit party, please contact a champion')
					}
				})
				.catch(err => {
					if (err.response !== undefined && err.response.status === 422) {
						this.showError(Object.values(err.response.data.errors).join("\n\n"))
					} else if (err.response !== undefined && err.response.status === 400) {
						this.showError(err.response.data.message)
					} else {
						this.showError()
					}
				})
				.finally(() => {
					window.scanner.loader.close()
				})
		},
		showError(msg = 'Big time Error, contact champion support', btnText = "") {
			this.shouldShowError = true
			this.errorMessage = msg
			this.errorBtnText = btnText
		},
		closeError() {
			this.shouldShowError = false
		}
	}
}
</script>

<style scoped>

.px-1 {
	padding-left: 1em;
	padding-right: 1em;
}

.py-1 {
	padding: 1em 0;
}

.pb-1 {
	padding-bottom: 1em;
}

.pt-0 {
	padding-top: 0;
}

#processParty.overlay-body { gap: 0; justify-content: start; }
#processParty .party-item-container-row { margin: 0; padding: 0.4em 0; }
#processParty .party-user {
	border-bottom: 1px solid grey;
	flex-grow: 1;
}

.overlay-header {
	padding-bottom: 0;
}

.grow {
	flex-grow: 1;
}
</style>